import { inject, Injectable, InjectionToken, provideAppInitializer, signal } from '@angular/core';
import { isNil } from 'es-toolkit';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, first, switchMap, take } from 'rxjs/operators';

import { State } from './machine';
import { OAuthService } from './oauth.service';
import { Platform } from './platform';
import { AuthConfig, WebPlatform } from './web-platform';

// Configuration token for dependency injection
export const PCC_AUTH_CONFIG = new InjectionToken<AuthConfig>('PCC_AUTH_CONFIG');

@Injectable({ providedIn: 'root' })
export class PccAuthService {
	private config: AuthConfig;

	private platform: Platform;

	private token = null;

	public expires: Observable<any>;

	isLoggingInWithPcc = signal(false);

	isLoggedInWithPcc = signal(false);

	private defaultConfig: AuthConfig = {
		scopes: ['openid', 'profile'],
		refreshDays: 1,
		notifyBeforeExp: 30,
		isMobile: false,
		restrictMobileUrls: false,
		preserveRoute: true,
		quickStart: true,
		checkExpiring: true,
	};

	oauthService = inject(OAuthService);

	public initialize(config: AuthConfig): void {
		this.config = { ...this.defaultConfig, ...config };
		this.platform = new WebPlatform(this.config);

		// Initialize OAuth service
		this.oauthService.initialize(this.config, this.platform);

		// Subscribe to token updates
		this.oauthService.getAccessToken().subscribe((token) => {
			this.token = token;
			this.isLoggedInWithPcc.set(!!token?.accessToken && token?.isValid);
		});

		this.oauthService.getStateChanges().subscribe((state) => this.isLoggingInWithPcc.set(['postCode'].includes(state?.type)));

		// Set up the expires observable
		this.expires = this.oauthService.getStateChanges().pipe(filter((state) => state?.type === 'refresh_expiring'));
	}

	public getToken(): any {
		return this.token;
	}

	public getStateChanges(): Observable<State> {
		return this.oauthService.getStateChanges();
	}

	public loaded(): Observable<{ accessToken: string; orgUuid: string; isValid: boolean }> {
		return this.oauthService.getAccessToken().pipe(
			filter((token) => !isNil(token?.accessToken)),
			first(),
		);
	}

	public getPccOrgUuid(): Observable<string> {
		return this.oauthService.getPccOrgUuid();
	}

	public refreshToken(): boolean {
		this.oauthService.dispatch('checkExpired');
		return true;
	}

	public refreshTokenAndWait(): Observable<{ accessToken: string; orgUuid: string; isValid: boolean }> {
		return this.oauthService.refreshTokenAndWait();
	}

	public async logout(): Promise<void> {
		if (!this.token) return;
		this.oauthService.dispatch('logout');
		const afterLogoutThenLoggedOut = this.oauthService.getStateChanges().pipe(
			filter((state) => state?.type === 'logout'),
			take(1),
			switchMap(() => this.oauthService.getStateChanges().pipe(filter((state) => state?.type === 'loggedOut'), take(1))),
		);
		await firstValueFrom(afterLogoutThenLoggedOut);
	}

	public run(state: string): void {
		this.oauthService.dispatch(state);
	}
}
/**
 * Provides PCC Auth configuration and initializes the service
 */
export function providePccAuth(config: AuthConfig) {
	return [
		PccAuthService,
		{ provide: PCC_AUTH_CONFIG, useValue: config },
		provideAppInitializer(() => {
			const authService = inject(PccAuthService);
			const authConfig = inject(PCC_AUTH_CONFIG);
			authService.initialize(authConfig);
		})
	];
}
